/**
 * Project interaction module
 * Handles hovering and clicking on project rows
 */

// Global state
let isProjectOpen = false;
let currentProject = null;
let currentGallery = null;
let currentImageIndex = 0;
let pendingProjectSlug = null;
const TRANSITION_DURATION = 250;

const getProjectSlugFromUrl = () => {
  const params = new URLSearchParams(window.location.search);
  return params.get('project') || (window.location.hash ? window.location.hash.substring(1) : null);
};

pendingProjectSlug = getProjectSlugFromUrl();

// Keep old hash links working, but normalize them to a server-readable share URL.
if (window.location.hash && pendingProjectSlug) {
  const canonicalUrl = new URL(window.location.href);
  canonicalUrl.search = '';
  canonicalUrl.searchParams.set('project', pendingProjectSlug);
  canonicalUrl.hash = '';
  window.history.replaceState(
    { section: 'projects', projectSlug: pendingProjectSlug },
    '',
    canonicalUrl.toString()
  );
}

document.addEventListener('DOMContentLoaded', () => {
  if (!pendingProjectSlug) return;

  const projectsSection = document.querySelector('.home__section--projects');
  const aboutSection = document.getElementById('about');
  const aboutButton = document.querySelector('[data-nav="about"]');
  const projectsButton = document.querySelector('[data-nav="projects"]');

  if (projectsSection) projectsSection.style.display = 'block';
  if (aboutSection) aboutSection.style.display = 'none';
  if (aboutButton) {
    aboutButton.classList.remove('header__menu__button--selected');
    aboutButton.removeAttribute('aria-current');
  }
  if (projectsButton) {
    projectsButton.classList.add('header__menu__button--selected');
    projectsButton.setAttribute('aria-current', 'page');
  }
});

const initProjectImageHover = () => {
  const tableRows = document.querySelectorAll('.home__section__table-row:not(.home__section__table-row--first)');
  const headerRow = document.querySelector('.home__section__table-row--first');
  const coverSection = document.querySelector('.home__section__cover');
  const projectsSection = document.querySelector('.home__section--projects');
  const projectDetails = document.querySelector('.home__section__project-details');
  const projectDescription = document.querySelector('.home__section__project-description');
  const backButton = document.querySelector('.home__section__back-button');
  const prevNav = document.querySelector('.home__section__cover-nav--prev');
  const nextNav = document.querySelector('.home__section__cover-nav--next');
  let closeTimer = null;
  let imageTimer = null;
  let imageRequestId = 0;
  let isImageTransitioning = false;
  let imageAnimations = [];
  
  if (!tableRows.length || !coverSection) {
    return;
  }
  
  // Try to force projects section to be visible if we have a hash fragment
  if (pendingProjectSlug) {
    projectsSection.style.display = 'block';
    const aboutSection = document.getElementById('about');
    if (aboutSection) {
      aboutSection.style.display = 'none';
    }
  }
  
  // Create image element
  let coverImage = new Image();
  coverImage.className = 'home__section__cover-image';
  coverImage.alt = '';
  coverImage.decoding = 'async';

  const applyImageData = (imageData, image = coverImage, mount = true) => {
    if (mount && !image.isConnected) {
      coverSection.appendChild(image);
    }

    image.srcset = imageData.srcset || '';
    image.sizes = imageData.sizes || '(min-width: 769px) 50vw, 100vw';
    image.width = imageData.width || 0;
    image.height = imageData.height || 0;
    image.alt = imageData.alt || '';
    image.src = imageData.url;
  };

  const cancelImageAnimations = () => {
    imageAnimations.forEach((animation) => animation.cancel());
    imageAnimations = [];
  };

  const clearCoverImage = () => {
    imageRequestId += 1;
    isImageTransitioning = false;
    cancelImageAnimations();

    coverSection.querySelectorAll('.home__section__cover-image').forEach((image) => {
      image.removeAttribute('src');
      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.removeAttribute('width');
      image.removeAttribute('height');
      image.alt = '';
      image.style.opacity = '';
      image.remove();
    });
  };
  
  // Handle URL changes
  const updateURL = (slug) => {
    const currentUrl = new URL(window.location.href);
    currentUrl.search = '';
    currentUrl.hash = '';

    if (slug) {
      currentUrl.searchParams.set('project', slug);
      history.pushState({ section: 'projects', projectSlug: slug }, '', currentUrl.toString());
    } else {
      currentUrl.search = '?projects';
      history.pushState({ section: 'projects', projectSlug: null }, '', currentUrl.toString());
    }
  };
  
  // Find a project row by slug
  const findProjectBySlug = (slug) => {
    if (!slug) {
      return null;
    }
    
    const match = Array.from(tableRows).find(row => row.dataset.slug === slug);
    return match;
  };
  
  // Directly check for and open project from URL
  const openProjectFromUrl = (attemptCount = 0) => {
    const slug = pendingProjectSlug || getProjectSlugFromUrl();
    if (!slug) {
      return false;
    }
    
    const matchingRow = findProjectBySlug(slug);
    if (matchingRow) {
      // Clear pending flag since we're handling it now
      pendingProjectSlug = null;
      
      // Force projects section to be visible
      projectsSection.style.display = 'block';
      const aboutSection = document.getElementById('about');
      if (aboutSection) {
        aboutSection.style.display = 'none';
      }
      
      // Open the project
      openProject(matchingRow, false);
      return true;
    } else {
      if (attemptCount < 10) {
        // Retry more times with increasing delay if rows aren't available yet
        const delay = 100 * (attemptCount + 1);
        setTimeout(() => openProjectFromUrl(attemptCount + 1), delay);
        return false;
      }
    }
    return false;
  };
  
  // Gallery navigation
  const showImage = (index, skipFade = false) => {
    if (!currentGallery.length || (isImageTransitioning && !skipFade)) return;

    if (imageTimer) {
      clearTimeout(imageTimer);
      imageTimer = null;
    }
    
    const targetIndex = (index + currentGallery.length) % currentGallery.length;
    const imageData = currentGallery[targetIndex];
    
    if (skipFade) {
      imageRequestId += 1;
      isImageTransitioning = false;
      cancelImageAnimations();
      currentImageIndex = targetIndex;
      coverSection.querySelectorAll('.home__section__cover-image').forEach((image) => {
        if (image !== coverImage) image.remove();
      });
      coverImage.style.transition = 'none';
      coverImage.style.opacity = 1;
      coverSection.style.height = '';
      applyImageData(imageData);
      
      // Make sure cover section is visible
      if (coverSection.style.opacity !== '1') {
        coverSection.style.opacity = 1;
      }
      
      requestAnimationFrame(() => {
        coverImage.style.transition = '';
      });
    } else {
      isImageTransitioning = true;
      const requestId = ++imageRequestId;
      const nextImage = new Image();
      let transitionStarted = false;

      nextImage.className = 'home__section__cover-image';
      nextImage.alt = '';
      nextImage.decoding = 'async';
      nextImage.style.opacity = 0;

      const startCrossfade = () => {
        if (
          transitionStarted ||
          requestId !== imageRequestId ||
          !isProjectOpen ||
          !nextImage.naturalWidth
        ) {
          return;
        }

        transitionStarted = true;
        coverSection.appendChild(nextImage);

        const previousImage = coverImage;
        previousImage.style.transition = 'none';
        nextImage.style.transition = 'none';
        nextImage.style.opacity = 1;
        currentImageIndex = targetIndex;

        imageAnimations = [
          previousImage.animate(
            [{ opacity: 1 }, { opacity: 0 }],
            { duration: TRANSITION_DURATION, easing: 'ease-in-out', fill: 'forwards' }
          ),
          nextImage.animate(
            [{ opacity: 0 }, { opacity: 1 }],
            { duration: TRANSITION_DURATION, easing: 'ease-in-out', fill: 'forwards' }
          )
        ];

        Promise.all(imageAnimations.map((animation) => animation.finished))
          .then(() => {
            if (requestId !== imageRequestId) return;
            nextImage.style.opacity = 1;
            imageAnimations.forEach((animation) => animation.cancel());
            imageAnimations = [];
            previousImage.remove();
            coverImage = nextImage;
            coverImage.style.transition = '';
            isImageTransitioning = false;
          })
          .catch(() => {
            // An interrupted transition is cleaned up by the next image request.
          });
      };

      nextImage.onload = startCrossfade;
      nextImage.onerror = () => {
        if (requestId === imageRequestId) {
          isImageTransitioning = false;
        }
      };
      applyImageData(imageData, nextImage, false);
      if (nextImage.complete) startCrossfade();
    }
  };
  
  const showNextImage = () => {
    showImage(currentImageIndex + 1);
  };
  
  const showPrevImage = () => {
    showImage(currentImageIndex - 1);
  };
  
  // Add gallery navigation events
  prevNav.addEventListener('click', showPrevImage);
  nextNav.addEventListener('click', showNextImage);

  coverSection.addEventListener('pointermove', (event) => {
    coverSection.style.setProperty('--gallery-cursor-x', `${event.clientX}px`);
    coverSection.style.setProperty('--gallery-cursor-y', `${event.clientY}px`);
  });

  // Handle scroll gradient visibility
  const handleScrollGradient = () => {
    if (!projectDescription) return;
    
    // Check projectDescription's scroll (it's the scroll container!)
    const scrollTop = projectDescription.scrollTop;
    const scrollHeight = projectDescription.scrollHeight;
    const clientHeight = projectDescription.clientHeight;
    
    // Check if there's scrollable content in projectDescription
    const hasScroll = scrollHeight > clientHeight;
    
    // Check if scrolled to bottom (with small threshold)
    const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;
    
    // Show gradient only if there's scroll AND we're not at the bottom
    if (!hasScroll || isAtBottom) {
      projectDescription.classList.add('scrolled-to-bottom');
    } else {
      projectDescription.classList.remove('scrolled-to-bottom');
    }
  };

  const setMetaContent = (selector, content) => {
    const element = document.querySelector(selector);
    if (element) element.setAttribute('content', content);
  };

  const updateDocumentMetadata = (row = null) => {
    const siteTitle = document.documentElement.dataset.siteTitle || 'Numeronovantasei';
    const siteDescription = document.documentElement.dataset.siteDescription || '';
    const title = row ? `${row.dataset.title} | ${siteTitle}` : siteTitle;
    const description = row ? row.dataset.metaDescription || siteDescription : siteDescription;
    const canonicalUrl = new URL(window.location.pathname, window.location.origin);
    canonicalUrl.search = row ? `?project=${encodeURIComponent(row.dataset.slug)}` : '?projects';
    const pageUrl = canonicalUrl.toString();

    document.title = title;
    setMetaContent('meta[name="description"]', description);
    setMetaContent('meta[property="og:title"]', title);
    setMetaContent('meta[property="og:description"]', description);
    setMetaContent('meta[property="og:url"]', pageUrl);
    setMetaContent('meta[property="twitter:title"]', title);
    setMetaContent('meta[property="twitter:description"]', description);
    setMetaContent('meta[property="twitter:url"]', pageUrl);

    let socialImage = row && currentGallery.length ? currentGallery[0].url : null;
    if (!socialImage && !row) {
      try {
        socialImage = JSON.parse(tableRows[0]?.dataset.gallery || '[]')[0]?.url || null;
      } catch (error) {
        socialImage = null;
      }
    }
    if (socialImage) {
      setMetaContent('meta[property="og:image"]', socialImage);
      setMetaContent('meta[property="twitter:image"]', socialImage);
    }

    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.href = pageUrl;
  };
  
  // Open project detail view
  const openProject = (row, updateHistory = true, focusBackButton = false) => {
    if (!row) {
      return;
    }

    if (closeTimer) {
      clearTimeout(closeTimer);
      closeTimer = null;
    }

    if (isProjectOpen && currentProject === row) {
      return;
    }
    
    isProjectOpen = true;
    currentProject = row;
    
    // Parse gallery data
    try {
      currentGallery = JSON.parse(row.dataset.gallery || '[]');
    } catch (e) {
      currentGallery = [];
    }
    projectsSection.classList.toggle('project-no-image', currentGallery.length === 0);
    
    // Update URL if needed
    if (updateHistory) {
      updateURL(row.dataset.slug);
    }
    updateDocumentMetadata(row);
    
    // Hide all rows except header
    tableRows.forEach(otherRow => {
      otherRow.style.display = 'none';
      otherRow.setAttribute('aria-expanded', 'false');
    });
    row.setAttribute('aria-expanded', 'true');
    headerRow.style.display = 'none';
    
    // Show project description
    projectDescription.innerHTML = row.dataset.description || '';
    projectDescription.scrollTop = 0;

    if (projectDescription._scrollHandler) {
      projectDescription.removeEventListener('scroll', projectDescription._scrollHandler);
    }
    
    // Add scroll listener for gradient - projectDescription is the scroll container
    const scrollHandler = () => {
      handleScrollGradient();
    };
    projectDescription.addEventListener('scroll', scrollHandler);
    
    // Store handler for cleanup
    projectDescription._scrollHandler = scrollHandler;
    
    // Switch both parts of the detail view immediately; CSS handles the short fade.
    projectsSection.classList.add('project-open');
    projectDetails.classList.add('active');
    handleScrollGradient();

    if (focusBackButton) {
      backButton.focus({ preventScroll: true });
    }
    
    // Check if the cover is already visible (from hover)
    const coverIsVisible = coverSection.style.opacity === '1';
    
    // Show first image - skip fade if already visible
    if (currentGallery.length > 0) {
      if (coverIsVisible) {
        // If we're already showing an image (from hover), and it's the same one
        // we want to display, just adjust dimensions without fading
        const firstGalleryImage = currentGallery[0].url;
        const sameImage = coverImage.src.endsWith(firstGalleryImage.split('/').pop());
        
        currentImageIndex = 0;
        showImage(0, sameImage);
      } else {
        // Preload the first image to get dimensions before showing
        const preloadImg = new Image();
        preloadImg.srcset = currentGallery[0].srcset || '';
        preloadImg.sizes = currentGallery[0].sizes || '(min-width: 769px) 50vw, 100vw';
        preloadImg.onload = () => {
          currentImageIndex = 0;
          showImage(0, true);
        };
        preloadImg.src = currentGallery[0].url;
      }
    } else {
      currentImageIndex = 0;
      coverSection.style.opacity = 0;
      clearCoverImage();
      coverSection.style.height = '';
    }
    
    // Show navigation if we have multiple images
    if (currentGallery.length > 1) {
      prevNav.style.display = 'block';
      nextNav.style.display = 'block';
    } else {
      prevNav.style.display = 'none';
      nextNav.style.display = 'none';
    }
  };
  
  // Close project detail view
  const closeProject = (updateHistory = true) => {
    if (!isProjectOpen && !projectsSection.classList.contains('project-open')) {
      return;
    }

    const projectToRestore = currentProject;
    isProjectOpen = false;
    currentProject = null;
    
    // Update URL
    if (updateHistory) {
      updateURL('');
    }
    updateDocumentMetadata();
    
    // Remove scroll listener from projectDescription
    if (projectDescription && projectDescription._scrollHandler) {
      projectDescription.removeEventListener('scroll', projectDescription._scrollHandler);
      projectDescription._scrollHandler = null;
    }
    
    // Make the outgoing detail non-interactive immediately.
    projectDetails.classList.remove('active');
    coverSection.style.opacity = 0;
    
    // Hide navigation immediately
    prevNav.style.display = 'none';
    nextNav.style.display = 'none';
    
    if (imageTimer) {
      clearTimeout(imageTimer);
      imageTimer = null;
    }

    // Change the layout only after the short fade has completed.
    closeTimer = setTimeout(() => {
      // Show all rows again
      tableRows.forEach(row => {
        row.style.display = 'flex';
        row.setAttribute('aria-expanded', 'false');
      });
      headerRow.style.display = 'flex';
      
      // Reset cover section (removes position: fixed, changes dimensions)
      projectsSection.classList.remove('project-open');
      projectsSection.classList.remove('project-no-image');
      
      // Reset cover section height
      coverSection.style.height = '';
      clearCoverImage();
      if (updateHistory && projectToRestore && getComputedStyle(projectsSection).display !== 'none') {
        projectToRestore.focus({ preventScroll: true });
      }
      closeTimer = null;
    }, TRANSITION_DURATION);
  };
  
  // Add back button event
  backButton.addEventListener('click', () => {
    closeProject();
  });
  
  // Listen for closeProject event
  document.addEventListener('closeProject', () => {
    if (isProjectOpen) {
      closeProject(false); // Don't update history as the section change already did that
    }
  });
  
  // Handle browser back button
  window.addEventListener('popstate', (event) => {
    const slug = getProjectSlugFromUrl();
    if (slug) {
      const matchingRow = findProjectBySlug(slug);
      
      if (matchingRow) {
        openProject(matchingRow, false);
      }
    } else if (isProjectOpen) {
      closeProject(false);
    }
  });
  
  // Column sorting
  const headerCells = headerRow.querySelectorAll('.home__section__table-cell');
  let currentSortCol = -1;
  let sortAsc = true;

  const MONTH_ORDER = {
    january: 1, february: 2, march: 3, april: 4, may: 5, june: 6,
    july: 7, august: 8, september: 9, october: 10, november: 11, december: 12,
    janvier: 1, février: 2, mars: 3, avril: 4, mai: 5, juin: 6,
    juillet: 7, août: 8, septembre: 9, octobre: 10, novembre: 11, décembre: 12
  };

  const parseDateValue = (text) => {
    const parts = text.trim().toLowerCase().split(/[\s-]+/);
    let month = 0;
    let year = 0;
    for (const part of parts) {
      if (MONTH_ORDER[part]) month = MONTH_ORDER[part];
      const num = parseInt(part, 10);
      if (num > 1900) year = num;
    }
    return year * 100 + month;
  };

  const isDateColumn = (colIndex) => colIndex === 3;

  const arrowSvg = (direction) => {
    const rotate = direction === 'desc' ? ' style="transform:rotate(180deg)"' : '';
    return `<svg class="sort-arrow" width="0.55em" height="0.55em" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg"${rotate}><path d="M6 2L6 10M6 2L2.5 5.5M6 2L9.5 5.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  };

  const sortRows = (colIndex) => {
    if (isProjectOpen) return;

    if (currentSortCol === colIndex) {
      sortAsc = !sortAsc;
    } else {
      currentSortCol = colIndex;
      sortAsc = true;
    }

    headerCells.forEach((cell, i) => {
      const existing = cell.querySelector('.sort-arrow');
      if (existing) existing.remove();
      cell.classList.remove('sort-active');
      cell.setAttribute('aria-sort', 'none');
      if (i === colIndex) {
        cell.classList.add('sort-active');
        cell.setAttribute('aria-sort', sortAsc ? 'ascending' : 'descending');
        cell.insertAdjacentHTML('beforeend', ' ' + arrowSvg(sortAsc ? 'asc' : 'desc'));
      }
    });

    const rowsArray = Array.from(tableRows);
    rowsArray.sort((a, b) => {
      const aText = a.children[colIndex]?.textContent.trim() || '';
      const bText = b.children[colIndex]?.textContent.trim() || '';
      let cmp;
      if (isDateColumn(colIndex)) {
        cmp = parseDateValue(aText) - parseDateValue(bText);
      } else {
        cmp = aText.toLowerCase().localeCompare(bText.toLowerCase(), undefined, { numeric: true });
      }
      return sortAsc ? cmp : -cmp;
    });

    const table = document.querySelector('.home__section__table');
    rowsArray.forEach(row => table.appendChild(row));
  };

  headerCells.forEach((cell, index) => {
    cell.style.cursor = 'pointer';
    cell.addEventListener('click', () => sortRows(index));
    cell.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        sortRows(index);
      }
    });
  });

  // Add row hover events
  tableRows.forEach(row => {
    // Only show hover preview when not in project detail view
    row.addEventListener('mouseenter', () => {
      if (isProjectOpen) return;
      
      let galleryData = [];
      try {
        galleryData = JSON.parse(row.dataset.gallery || '[]');
      } catch (e) {
        return;
      }
      
      if (galleryData.length > 0) {
        const imageData = galleryData[0];
        
        // If an image is already visible and we're changing to a different one,
        // use the same fade effect for consistency
        if (coverSection.style.opacity === '1' && coverImage.src !== imageData.url) {
          coverSection.style.opacity = 0;
          imageTimer = setTimeout(() => {
            applyImageData(imageData);
            coverSection.style.opacity = 1;
            imageTimer = null;
          }, TRANSITION_DURATION);
        } else {
          // No fade needed for first display
          applyImageData(imageData);
          coverSection.style.opacity = 1;
        }
      } else {
        if (imageTimer) {
          clearTimeout(imageTimer);
          imageTimer = null;
        }
        coverSection.style.opacity = 0;
        clearCoverImage();
      }
    });
    
    row.addEventListener('mouseleave', () => {
      if (isProjectOpen) return;
      if (imageTimer) {
        clearTimeout(imageTimer);
        imageTimer = null;
      }
      coverSection.style.opacity = 0;
    });
    
    // Add click event
    row.addEventListener('click', () => {
      if (!isProjectOpen) {
        openProject(row);
      }
    });

    row.addEventListener('keydown', (event) => {
      if ((event.key === 'Enter' || event.key === ' ') && !isProjectOpen) {
        event.preventDefault();
        openProject(row, true, true);
      }
    });
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && isProjectOpen) {
      event.preventDefault();
      closeProject();
    }
  });
  
  // Initialize project from URL if needed - try immediately and with retries
  if (pendingProjectSlug) {
    const matchingRow = findProjectBySlug(pendingProjectSlug);
    if (matchingRow && !isProjectOpen) {
      pendingProjectSlug = null;
      openProject(matchingRow, false);
    }
  } else if (getProjectSlugFromUrl()) {
    // Try immediately - if it works, no need for retries
    if (!openProjectFromUrl(0)) {
      // If it didn't work, set up retries with increasing delays
      for (let i = 1; i <= 5; i++) {
        setTimeout(() => openProjectFromUrl(i), 200 * i);
      }
    }
  }
  
  // Also trigger when DOM is fully loaded and after window load
  document.addEventListener('DOMContentLoaded', () => {
    if (getProjectSlugFromUrl()) {
      openProjectFromUrl(0);
    }
  });
  
  window.addEventListener('load', () => {
    if (getProjectSlugFromUrl()) {
      openProjectFromUrl(0);
    }
  });

  document.addEventListener('hashDetected', (event) => {
    const slug = event.detail && event.detail.hash;
    const matchingRow = findProjectBySlug(slug);

    if (matchingRow) {
      pendingProjectSlug = null;
      openProject(matchingRow, false);
    }
  });
};

// Attach this function to the global window for manual testing
window.testOpenProject = function(slug) {
  // Try to find and open a project with this slug
  // This can be called from the browser console to test project opening
  const tableRows = document.querySelectorAll('.home__section__table-row:not(.home__section__table-row--first)');
  
  if (!tableRows.length) {
    return false;
  }
  
  const matchingRow = Array.from(tableRows).find(row => row.dataset.slug === slug);
  
  if (matchingRow) {
    // Force display of projects section
    const projectsSection = document.querySelector('.home__section--projects');
    const aboutSection = document.getElementById('about');
    
    if (projectsSection) projectsSection.style.display = 'block';
    if (aboutSection) aboutSection.style.display = 'none';
    
    // Trigger hash detected event
    document.dispatchEvent(new CustomEvent('hashDetected', { 
      detail: { hash: slug } 
    }));
    
    return true;
  } else {
    return false;
  }
};

// Global event listener for hash changes
document.addEventListener('hashDetected', function(e) {
  if (e.detail && e.detail.hash) {
    const slug = e.detail.hash;
    pendingProjectSlug = slug;
    
    // Force projects section to be visible
    const projectsSection = document.querySelector('.home__section--projects');
    const aboutSection = document.getElementById('about');
    
    if (projectsSection) {
      projectsSection.style.display = 'block';
    }
    
    if (aboutSection) {
      aboutSection.style.display = 'none';
    }
    
    // The actual project opening will be handled during module initialization
  }
});

export default initProjectImageHover; 